import { Box, Button, useToast } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import React, { useEffect, useRef, useState } from 'react'

// components
import { ModalBuild } from 'components/molecules'
import { FileGenerator } from 'components/molecules/FileGenerator'
import { ListCardMetrics, Option } from 'components/molecules/ListCardMetrics'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { TableBuild } from 'components/organisms'
import { TableBuildProps } from 'components/organisms/TableBuild/interfaces'
import ModalBatchUpload from 'pages/Contracts/Components/ModalBatchUpload'
import { DocumentTypes } from 'pages/Report/types'
import ServiceFee from 'services/fee'
import { generateFile } from 'utils/blob'
import formatter from 'utils/formatter'
import { DataDuda, FeeDetran, FeeDetranQuery } from '../../types'
import { FilterFeeManagement } from '../FilterFeeManagement/FilterFeeManagement'
import { FilterFee } from '../FilterFeeManagement/interfaces'

const DEFAULT_UF = 'RJ'
const UF_RS = 'RS'

const Detran: React.FC = () => {
    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState(10)
    const [totalPages, setTotalPages] = useState(1)
    const [filter, setFilter] = useState<FilterFee>({
        uf: DEFAULT_UF,
        cnpj: '',
        status: '',
        numeroTaxa: ''
    })
    const [loadingFile, setLoadingFile] = useState<boolean>(false)
    const [cardData, setCardData] = useState<DataDuda>()
    const [table, setTable] = useState<
        TableBuildProps<
            | 'ufTaxa'
            | 'numeroTaxa'
            | 'cnpj'
            | 'dataCriacao'
            | 'dataAlteracao'
            | 'status'
            | 'valor'
        >
    >({
        header: [
            {
                name: 'ufTaxa',
                text: 'UF do Documento',
                position: 0
            },
            {
                name: 'numeroTaxa',
                text: 'N° Documento',
                position: 1
            },
            {
                name: 'cnpj',
                text: 'CNPJ Agente',
                position: 2
            },
            {
                name: 'dataCriacao',
                text: 'Data Importação',
                position: 3
            },
            {
                name: 'dataAlteracao',
                text: 'Data Registro',
                position: 4
            },
            {
                name: 'valor',
                text: 'Valor',
                position: 5
            },
            {
                name: 'status',
                text: 'Status',
                position: 6
            }
        ]
    })

    const modalUploadRef = useRef<ModalBuildHandle>(null)

    const handleOpenUploadFileModal = () => {
        modalUploadRef.current?.onOpen()
    }

    const handleCloseUploadFileModal = () => {
        modalUploadRef.current?.onClose()
    }

    const toast = useToast()
    const [loading, setLoading] = useState(false)

    const selectedUf = (filter?.uf || DEFAULT_UF).toUpperCase()
    const isRS = selectedUf === UF_RS

    const optionsMetrics = (data?: DataDuda): Option[] => {
        if (!data) return []

        return [
            {
                value: data.qtdTaxasDisponiveis,
                label: 'Taxas disponiveis'
            },
            {
                value: data.qtdTaxasIndisponiveis,
                label: 'Taxas utilizadas'
            },
            {
                value: data.qtdTaxasFalhas,
                label: 'Taxas com falhas'
            }
        ]
    }

    const buildTable = (fee: FeeDetran[]) => {
        return fee.map(
            ({ dataCriacao, dataAlteracao, cnpj, valor, ...data }) => ({
                ...data,
                cnpj: cnpj ? formatter.cnpj(cnpj) : '-',
                dataCriacao: dataCriacao ? formatter.data(dataCriacao) : '',
                valor:
                    valor !== null && valor !== undefined
                        ? formatter.moeda(valor)
                        : '-',
                dataAlteracao: dataAlteracao
                    ? formatter.data(dataAlteracao)
                    : ''
            })
        )
    }

    const normalizeFilter = (value?: Partial<FilterFee>): FeeDetranQuery => {
        return {
            uf: value?.uf || DEFAULT_UF,
            cnpj: value?.cnpj ? value.cnpj.replace(/\D/g, '') : '',
            status: value?.status ?? '',
            numeroTaxa: value?.numeroTaxa ?? ''
            }
    }

    const getQueryRJ = (query: FeeDetranQuery): FeeDetranQuery => {
        return {
            cnpj: query.cnpj,
            status: query.status,
            numeroTaxa: query.numeroTaxa
        }
    }

    const filterLocalRS = (
        taxas: FeeDetran[],
        query: FeeDetranQuery
    ): FeeDetran[] => {
        return taxas.filter(taxa => {
            const sameStatus = query.status
                ? taxa.status === query.status
                : true

            const sameNumeroTaxa = query.numeroTaxa
                ? taxa.numeroTaxa === query.numeroTaxa
                : true

            return sameStatus && sameNumeroTaxa
        })
    }

    const paginateLocal = (
        taxas: FeeDetran[],
        currentPage: number,
        currentLimit: number
    ): FeeDetran[] => {
        const start = (currentPage - 1) * currentLimit
        const end = start + currentLimit

        return taxas.slice(start, end)
    }

    async function fetchListFee(
        query?: FeeDetranQuery,
        currentPage = page,
        currentLimit = limit
    ) {
        setLoading(true)

        const normalizedQuery = normalizeFilter(query)
        const uf = (normalizedQuery.uf || DEFAULT_UF).toUpperCase()

        try {
            if (uf === UF_RS) {
                if (!normalizedQuery.cnpj) {
                    setCardData({
                        qtdTaxasDisponiveis: 0,
                        qtdTaxasFalhas: 0,
                        qtdTaxasIndisponiveis: 0
                    })

                    setTable((old: any) => ({
                        ...old,
                        body: []
                    }))

                    setTotalPages(1)

                    toast({
                        title: 'Informe o CNPJ',
                        description:
                            'Para consultar taxas do Detran RS, informe o CNPJ do agente financeiro.',
                        position: 'bottom-left',
                        status: 'warning',
                        isClosable: true
                    })

                    return
                }

                const response = await ServiceFee.getFeeDetranRS({
                    pagina: currentPage,
                    limite: currentLimit,
                    query: normalizedQuery
                })

                const taxasRS = filterLocalRS(
                    response.data.taxas || [],
                    normalizedQuery
                )

                const totalPagesRS = Math.max(
                    1,
                    Math.ceil(taxasRS.length / currentLimit)
                )

                const taxasPaginadas = paginateLocal(
                    taxasRS,
                    currentPage,
                    currentLimit
                )

                setTotalPages(totalPagesRS)

                const {
                    qtdTaxasDisponiveis,
                    qtdTaxasFalhas,
                    qtdTaxasIndisponiveis
                } = response.data

                setCardData({
                    qtdTaxasDisponiveis,
                    qtdTaxasFalhas,
                    qtdTaxasIndisponiveis
                })

                setTable((old: any) => ({
                    ...old,
                    body: buildTable(taxasPaginadas)
                }))

                return
            }

            const response = await ServiceFee.getFee({
                pagina: currentPage,
                limite: currentLimit,
                query: getQueryRJ(normalizedQuery)
            })

            setTotalPages(+response.headers['total-pages'])

            const fee = response.data.taxas

            const {
                data: {
                    qtdTaxasDisponiveis,
                    qtdTaxasFalhas,
                    qtdTaxasIndisponiveis
                }
            } = response

            setCardData({
                qtdTaxasDisponiveis,
                qtdTaxasFalhas,
                qtdTaxasIndisponiveis
            })

            setTable((old: any) => ({
                ...old,
                body: buildTable(fee)
            }))
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description: 'Ocorreu algum erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoading(false)
        }
    }

    const handlePage = (value: number) => {
        setPage(value)
        setLoading(true)
    }

    const handleLimit = (value: number) => {
        setLimit(value)
        setLoading(true)
    }

    useEffect(() => {
        fetchListFee(normalizeFilter(filter), page, limit)
    }, [page, limit])

    const handleFilterFee = (value: FilterFee) => {
        const normalized = normalizeFilter(value) as FilterFee

        setLoading(true)
        setPage(1)
        setFilter(normalized)

        fetchListFee(normalized, 1, limit)
    }

    const callAPI = (type: DocumentTypes) => {
        downloadCsv(type)
    }

    const downloadCsv = async (type: DocumentTypes) => {
        if (isRS) {
            toast({
                title: 'Exportação indisponível',
                description:
                    'A exportação de relatório para taxas do Detran RS ainda não está disponível.',
                position: 'bottom-left',
                status: 'info',
                isClosable: true
            })

            return
        }

        try {
            setLoadingFile(true)

            const response = await ServiceFee.getCSV({
                cnpj: filter?.cnpj ? filter.cnpj.replace(/\D/g, '') : '',
                status: filter?.status ?? '',
                numeroTaxa: filter?.numeroTaxa ?? '',
                tipo: type
            })

            generateFile(type, response)

            toast({
                title: 'Arquivo gerado com sucesso!',
                description: `O arquivo ${type} foi gerado conforme os filtros selecionados. Verifique sua pasta de downloads para acessá-lo.`,
                position: 'bottom-left',
                status: 'success',
                isClosable: true
            })
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description: 'Ocorreu um erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoadingFile(false)
        }
    }

    return (
        <>
            <FilterFeeManagement
                type="detran"
                handleFilterFee={handleFilterFee}
            />

            <ModalBuild
                ref={modalUploadRef}
                closeOnOverlayClick={false}
                title=" "
                size="2xl"
            >
                <ModalBatchUpload
                    isManagementFee
                    onClose={handleCloseUploadFileModal}
                />
            </ModalBuild>

            <Box display="flex" justifyContent="space-between" marginTop="24px">
                <ListCardMetrics
                    options={optionsMetrics(cardData)}
                    isLoading={loading}
                />

                {!isRS && (
                    <Box>
                        <Button
                            colorScheme="primary"
                            onClick={handleOpenUploadFileModal}
                            marginRight="4px"
                        >
                            Importar
                        </Button>
                        <FileGenerator
                            callAPI={callAPI}
                            loading={loadingFile}
                        />
                    </Box>
                )}
            </Box>

            <Box
                as={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                <TableBuild
                    header={table.header}
                    body={table.body}
                    action={table.action}
                    loading={{
                        status: loading,
                        loader: [
                            {
                                height: '20px'
                            },
                            {
                                height: '20px'
                            },
                            {
                                height: '20px'
                            },
                            {
                                height: '20px'
                            },
                            {
                                height: '20px'
                            },
                            {
                                height: '20px'
                            },
                            {
                                height: '20px'
                            }
                        ]
                    }}
                    pagination={{
                        page,
                        limit,
                        totalPages
                    }}
                    onChangePage={handlePage}
                    onChangeLimit={handleLimit}
                ></TableBuild>
            </Box>
        </>
    )
}

export default Detran